'use client';

import React from 'react';
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/Footer';
import Icon from '@/components/ui/AppIcon';
import { useTranslation } from '@/hooks/useTranslation';

export default function GDPRCompliancePage() {
    const { t } = useTranslation();

    const rights = [
        { icon: 'DocumentTextIcon', title: t('rightToAccess'), desc: t('rightToAccessDesc') },
        { icon: 'PencilSquareIcon', title: t('rightToRectification'), desc: t('rightToRectificationDesc') },
        { icon: 'TrashIcon', title: t('rightToErasure'), desc: t('rightToErasureDesc') },
        { icon: 'ArrowsRightLeftIcon', title: t('rightToDataPortability'), desc: t('rightToDataPortabilityDesc') },
    ];

    return (
        <>
            <Header />
            <main className="min-h-screen bg-background">
                <section className="py-20 px-6 bg-gradient-to-b from-card to-background border-b border-border">
                    <div className="max-w-4xl mx-auto text-center">
                        <div className="inline-flex items-center space-x-2 px-4 py-2 bg-verified/10 rounded-full text-verified text-sm font-semibold mb-6">
                            <Icon name="ShieldCheckIcon" size={16} variant="solid" />
                            <span>{t('gdprCompliantBadge')}</span>
                        </div>
                        <h1 className="text-5xl font-headline font-bold text-foreground mb-6">{t('gdprComplianceTitle')}</h1>
                        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                            {t('gdprComplianceSubtitle')}
                        </p>
                    </div>
                </section>

                {/* Rights */}
                <section className="py-20 px-6">
                    <div className="max-w-5xl mx-auto">
                        <h2 className="text-3xl font-headline font-bold text-center mb-12">{t('yourRightsUnderGDPR')}</h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            {rights.map((right, i) => (
                                <div key={i} className="bg-card border border-border rounded-lg p-6">
                                    <Icon name={right.icon as any} size={32} variant="outline" className="text-primary mb-4" />
                                    <h3 className="font-headline font-semibold text-foreground mb-2">{right.title}</h3>
                                    <p className="text-sm text-muted-foreground">{right.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Data Protection */}
                <section className="py-20 px-6 bg-card">
                    <div className="max-w-4xl mx-auto">
                        <h2 className="text-3xl font-headline font-bold text-center mb-12">{t('dataProtectionMeasures')}</h2>
                        <ul className="space-y-3 text-muted-foreground">
                            <li className="flex items-start gap-3">
                                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-verified flex-shrink-0 mt-0.5" />
                                <span>{t('dataProtection1')}</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-verified flex-shrink-0 mt-0.5" />
                                <span>{t('dataProtection2')}</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-verified flex-shrink-0 mt-0.5" />
                                <span>{t('dataProtection3')}</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-verified flex-shrink-0 mt-0.5" />
                                <span>{t('dataProtection4')}</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-verified flex-shrink-0 mt-0.5" />
                                <span>{t('dataProtection5')}</span>
                            </li>
                        </ul>
                    </div>
                </section>

                {/* Contact DPO */}
                <section className="py-20 px-6">
                    <div className="max-w-4xl mx-auto bg-card border border-border rounded-lg p-12 text-center">
                        <Icon name="EnvelopeIcon" size={48} variant="outline" className="text-primary mx-auto mb-6" />
                        <h2 className="text-2xl font-headline font-bold text-foreground mb-4">{t('contactDPO')}</h2>
                        <p className="text-muted-foreground mb-6">
                            {t('contactDPODesc')}
                        </p>
                        <a href="mailto:dpo@egyptexgateway.com" className="text-primary font-semibold hover:underline">
                            dpo@egyptexgateway.com
                        </a>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
